const API_BASE = "https://api.scryfall.com";

export type Card = {
  name: string;
  mana_cost?: string;
  type_line?: string;
  oracle_text?: string;
  power?: string;
  toughness?: string;
  loyalty?: string;
  prices?: { usd?: string };
  legalities?: Record<string, string>;
};

function formatCardInfo(card: Card): string {
  const info = [
    `Name: ${card.name || 'Unknown'}`,
    `Mana Cost: ${card.mana_cost || 'N/A'}`,
    `Type: ${card.type_line || 'Unknown'}`
  ];

  if (card.oracle_text) info.push(`Text: ${card.oracle_text}`);
  if (card.power) info.push(`Power/Toughness: ${card.power}/${card.toughness}`);
  if (card.loyalty) info.push(`Loyalty: ${card.loyalty}`);

  return info.join('\n');
}

/**
 * Look up a card by name using Scryfall API (client-side)
 */
export async function lookupCard(name: string): Promise<string> {
  try {
    const url = `${API_BASE}/cards/named?fuzzy=${encodeURIComponent(name)}`;
    const res = await fetch(url);
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ details: res.statusText }));
      return `[Card not found: ${name}] ${err.details || err.message || 'Unknown error'}`;
    }
    
    const card: Card = await res.json();
    return formatCardInfo(card);
  } catch (e) {
    return `[Error looking up ${name}]: ${String(e)}`;
  }
}

/**
 * Fetch full card data by name (for tooltip)
 */
export async function fetchCardData(name: string): Promise<Scryfall.Card | null> {
  try {
    const url = `${API_BASE}/cards/named?fuzzy=${encodeURIComponent(name)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    console.error("Error fetching card data:", e);
    return null;
  }
}

/**
 * Parse [[Card Name]] mentions from text and return card names
 * Supports: [[Card Name]]
 */
export function parseCardMentions(text: string): string[] {
  const mentions: string[] = [];
  
  // Match [[Card Name]]
  const regex = /\[\[([^\]]+)\]\]/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    const cardName = match[1]?.trim();
    if (cardName) {
      mentions.push(cardName);
    }
  }
  
  return mentions;
}

/**
 * Look up all mentioned cards and return formatted context
 */
export async function getCardContext(mentions: string[]): Promise<string> {
  if (mentions.length === 0) return "";
  
  const results = await Promise.all(mentions.map(lookupCard));
  return "Card Information:\n" + results.join("\n\n---\n\n");
}
